import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import SkeletonGroup from '@/components/motion/SkeletonGroup'
import Change from '@/components/ui/Change'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useIndexSymbols, useSignalHistory } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { formatDate, formatMoney } from '@/lib/format'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { ClosedTrade } from '@/schemas/signals'
import MissingFigure from '@/screens/signals/components/MissingFigure'
import { signalCompanyColumn } from '@/screens/signals/components/SignalCompanyColumn'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsFootnote from '@/screens/signals/components/SignalsFootnote'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import PastSessionEmpty from '@/screens/signals/components/PastSessionEmpty'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import SignalsSearchBox from '@/screens/signals/components/SignalsSearchBox'
import { PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import SignalsTableFrame from '@/screens/signals/components/SignalsTableFrame'
import {
  FootnoteSkeleton,
  SearchSkeleton,
  TableFrameSkeleton,
  ToolbarSkeleton,
} from '@/screens/signals/components/SignalsTableSkeletons'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { useSearchBox } from '@/screens/signals/lib/search-box'
import { SIGNAL_ROWS_60 } from '@/screens/signals/lib/signal-rows'
import { useIsPastSession } from '@/screens/signals/lib/session-status'
import { useShownValue } from '@/screens/signals/lib/shown-value'
import { isSignalsLocked, signalRowLink } from '@/screens/signals/lib/signals-ui'
import {
  historyQueryOf,
  loadSignalHistory,
  signalHistorySearchSchema,
} from '@/screens/signals/SignalHistoryScreen.loader'
import { SIGNAL_DEFAULT_PAGE_SIZE, toSignalPageSize } from '@/screens/signals/search'

const TITLE = 'Signal History'

/** The loader's own page size (`signalHistorySearchSchema`), so a bare URL is this tall. */
const SKELETON_ROWS = SIGNAL_DEFAULT_PAGE_SIZE

/** The columns the table holds, lead one included. */
const COLUMN_COUNT = 6

/*
 * The loaded page, block for block: the search and date row, the panel of
 * fifteen two-line rows (36px head, 60px rows, the mock's), its pager and the D1
 * price-basis line (one line from `@xl`, two below).
 */
function SignalHistoryPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading signal history…"
      header={<TitleRow title={TITLE} />}
      {...signalsRailProps(TITLE)}
    >
      <TableFrameSkeleton
        rows={SKELETON_ROWS}
        columns={COLUMN_COUNT}
        toolbar={<ToolbarSkeleton start={<SearchSkeleton />} end={<PickerSkeleton />} />}
        tableClassName={SIGNAL_ROWS_60}
      />
      <FootnoteSkeleton className="h-8 @xl:h-4" />
    </ScreenLayout>
  )
}

function SignalHistoryError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={<TitleRow title={TITLE} />}
      pageTitle={TITLE}
      title="Signal history did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/history')({
  validateSearch: signalHistorySearchSchema,
  loader: loadSignalHistory,
  pendingComponent: SignalHistoryPending,
  errorComponent: SignalHistoryError,
  component: SignalHistoryScreen,
})

/**
 * The mock's columns (`COMPARE-sz2` §4): 12/400, the four figure columns right-aligned and hugging
 * the right edge (dates 100, prices 90, Return 80), the entry side in the secondary grey and the
 * exit price in the primary ink, Return in the market colours; the Company column takes the rest.
 */
const COLUMNS: readonly DataTableColumn<ClosedTrade>[] = [
  signalCompanyColumn<ClosedTrade>({ fill: true }),
  {
    id: 'entryDate',
    accessorFn: (row) => row.entryDate,
    header: 'Entry date',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: '112px', hideBelow: 'md' },
    cell: ({ row }) => formatDate(row.original.entryDate),
  },
  {
    id: 'exitDate',
    accessorFn: (row) => row.exitDate,
    header: 'Exit date',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: '112px', hideBelow: 'md' },
    cell: ({ row }) => formatDate(row.original.exitDate),
  },
  {
    id: 'entryPrice',
    accessorFn: (row) => row.entryPrice,
    header: 'Entry price',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: '102px' },
    cell: ({ row }) =>
      row.original.entryPrice === null ? <MissingFigure /> : formatMoney(row.original.entryPrice),
  },
  {
    id: 'exitPrice',
    accessorFn: (row) => row.exitPrice,
    header: 'Exit price',
    enableSorting: false,
    meta: { numeric: true, width: '102px' },
    cell: ({ row }) =>
      row.original.exitPrice === null ? <MissingFigure /> : formatMoney(row.original.exitPrice),
  },
  {
    id: 'return',
    accessorFn: (row) => row.returnPercent,
    header: 'Return',
    enableSorting: false,
    meta: { numeric: true, width: '80px' },
    cell: ({ row }) =>
      row.original.returnPercent === null ? (
        <MissingFigure />
      ) : (
        <Change value={row.original.returnPercent} percent />
      ),
  },
]

/**
 * Signal Zone · Signal history — the closed green → red pairs of the last two
 * years, most recent exit first (the API's order, D6), a page at a time.
 *
 * MAIN read keyed on `?q=`, `?page=` and `?pageSize=`, so `useQuery` +
 * `keepPreviousData`, not suspense (docs/route-loading.md, "Search params"):
 * turning a page or typing a search keeps the old rows under RefreshRegion's
 * refresh line until the new ones land. The search box follows `?q=` on Back
 * and Forward, as Trend's does.
 *
 * Entry is the green signal day's low and exit the red day's high (D1), so
 * every return is the best case; the line under the table says so. A search
 * restarts at page 1, since a page number means nothing against a new result.
 * The date at the toolbar's right is the session picker. The read takes no date, so it serves
 * the latest session and a past pick shows `PastSessionEmpty` (`asks/signals-dates.md`).
 */
function SignalHistoryScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const [term, setTerm] = useSearchBox(search.q, (q) => {
    void navigate({ search: (prev) => ({ ...prev, q, page: 1 }), replace: true })
  })

  const query = useSignalHistory(historyQueryOf(search))
  const view = resolveViewState(query, { online })
  const result = query.data
  const isUpdating = query.isPlaceholderData
  const indexSymbols = useIndexSymbols()
  // The search the answer on screen is for, while a new one loads.
  const shownQ = useShownValue(search.q, isUpdating)

  const past = useIsPastSession(search.date)

  const goToPage = (page: number) => {
    void navigate({ search: (prev) => ({ ...prev, page }) })
  }

  const body = (() => {
    if (past && search.date !== undefined) {
      return <PastSessionEmpty date={search.date} size="section" />
    }
    if (view === 'failed' || view === 'offline') {
      return isSignalsLocked(query.error) ? (
        <SignalsLocked error={query.error} />
      ) : (
        <EmptyState
          kind={view}
          size="section"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          {...(view === 'failed' ? { title: 'Couldn’t load signal history' } : {})}
        />
      )
    }
    if (result === undefined) {
      // Only before any answer at all: the loader has one for the entry URL.
      return (
        <SkeletonGroup>
          <DataTableSkeleton
            rows={SKELETON_ROWS}
            columns={COLUMN_COUNT}
            rowHeight="lg"
            className={SIGNAL_ROWS_60}
          />
        </SkeletonGroup>
      )
    }
    if (result.rows.length === 0) {
      return (
        <RefreshRegion updating={isUpdating}>
          {result.total > 0 ? (
            // A hand-edited or stale `?page=` past the last page: the answer is empty but the
            // history is not.
            <SignalsEmpty
              reason="past-end"
              size="section"
              of="the history"
              onFirstPage={() => {
                goToPage(1)
              }}
            />
          ) : shownQ === undefined ? (
            <SignalsEmpty
              reason="list"
              size="section"
              title="No closed signals yet"
              description="A trade closes when a stock’s green signal is followed by a red one. None have closed in the last two years."
            />
          ) : (
            <EmptyState kind="no-results" size="section" query={shownQ} />
          )}
        </RefreshRegion>
      )
    }
    return (
      <RefreshRegion updating={isUpdating}>
        <DataTable
          columns={COLUMNS}
          rows={result.rows}
          // Entry date alone is not unique for a stock that re-enters, and a pair is
          // unique by its two dates together.
          getRowId={(row) => `${row.stock.symbol}-${row.entryDate}-${row.exitDate}`}
          rowLinkOptions={signalRowLink(indexSymbols)}
          caption="Closed signal trades, most recent exit first"
          manualSorting
          rowHeight="lg"
          headWeight="regular"
          washTone="wash"
          nameUnderline={false}
          className={SIGNAL_ROWS_60}
        />
      </RefreshRegion>
    )
  })()

  const hasTable =
    !past &&
    result !== undefined &&
    result.rows.length > 0 &&
    view !== 'failed' &&
    view !== 'offline'

  return (
    <ScreenLayout header={<TitleRow title={TITLE} />} {...signalsRailProps(TITLE)}>
      <SignalsTableFrame
        toolbar={<SignalsSearchBox value={term} onChange={setTerm} />}
        toolbarEnd={
          <>
            <UpdatingNote when={isUpdating} label="Updating the history…" />
            <SessionDatePicker
              value={search.date ?? null}
              onChange={(date) => {
                void navigate({ search: (prev) => ({ ...prev, date, page: 1 }) })
              }}
            />
          </>
        }
        {...(hasTable
          ? {
              paging: {
                page: result.page,
                pageSize: result.pageSize,
                total: result.total,
                unit: 'trades',
                onPageChange: goToPage,
                onPageSizeChange: (next) => {
                  void navigate({
                    search: (prev) => ({
                      ...prev,
                      pageSize: toSignalPageSize(next, prev.pageSize),
                      page: 1,
                    }),
                  })
                },
              },
            }
          : {})}
      >
        {body}
      </SignalsTableFrame>

      {hasTable ? <SignalsFootnote basis="history" /> : null}
    </ScreenLayout>
  )
}
